import React, { useEffect, useState, useMemo } from 'react';
import dayjs from "dayjs";
import { FiCalendar, FiCheck, FiX } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import LocateBed from "../../../../assets/images/locate-bed.svg";
import createAxiosInstance from "../../../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../../../utils/api";
import { getOther, getElements, responseTextByType } from "../../../../utils/helper"
import { formatDateTime } from "../../../../utils/formatters"
import { Agency } from '../../../../type/Agency';
import { showToast } from '../../../../Components/ToastNotification';
import useSocket from '../../../../services/socketService';

type BedNeededProps = {
  myShelter: Agency | null;
  agenciesById: any;
  updateIsBedNeededLoading: () => void
  updateCachedResponseId: (id: string | null) => void;
};

const BedNeeded: React.FC<BedNeededProps> = ({ myShelter, agenciesById, updateIsBedNeededLoading, updateCachedResponseId }) => {

  const [isExpandedArray, setIsExpandedArray] = useState([false, false, false]);
  const [request, setRequest] = useState([]);
  const [lastResponseList, setLastResponseList] = useState([]);
  const [isLoding, setIsLoding] = useState(false);

  const axiosInstance = createAxiosInstance();

  const { on } = useSocket("needs");

  useEffect(() => {
    const unsubscribe = on("message", (data) => {
      if (data.eventType === "request") {
        showToast("An agency has a new request.", "info");
        getRequestFromOthers();
      }
      if (data.eventType === "updated" || data.eventType === "canceled") {
        showToast("An agency has changed the status of a request.", "info");
        getRequestFromOthers();
      }
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (myShelter) {
      getRequestFromOthers();
    }
  }, [myShelter]);

  // Toggle function for specific card
  const toggleDetails = (index: number) => {
    setIsExpandedArray((prev) =>
      prev.map((state, i) => (i === index ? !state : state))
    );
  };

  const getRequestFromOthers = async () => {
    try {
      const response = await axiosInstance.get(`${API_ENDPOINTS.BEDFINDER.GETREQUESTFROMOTHERS}?status=open&expand=need/assessments,responses`);

      const data = response.data; // Extract the actual data

      const requests = data?.filter(
        (request: any) =>
          !request.responses?.find(
            (response: any) =>
              response.agencyId === myShelter?.agencyId &&
              response.response !== "null",
          ),
      ).reverse();

      const expandedArray: any = [];
      requests.forEach(() => {
        expandedArray.push(false)
      });

      // console.log(requests);
      setRequest(requests);
      setIsExpandedArray(expandedArray);
      getLastResponsesList();
    } catch (error) {
      console.log(error);
    }
  };

  const getLastResponsesList = async () => {
    try {
      const response = await axiosInstance.get(`${API_ENDPOINTS.BEDFINDER.GETREQUESTFROMOTHERS}?status=open&expand=responses`);

      const data = response.data; // Extract the actual data

      // console.log(requests);
      setLastResponseList(data);
      updateIsBedNeededLoading();
    } catch (error) {
      console.log(error);
    }
  }

  const lastResponseListData = useMemo(
    () =>
      lastResponseList
        ?.filter(
          (req: any) =>
            req?.responses?.some(
              (response: any) =>
                response?.response != "null" &&
                response?.agencyId == myShelter?.agencyId &&
                typeof myShelter == "object" &&
                !!agenciesById[req.agencyId]?.name == true,
            ),
        )
        ?.map((it: any) => ({ ...it, createdAt: dayjs(it.createdAt) }))
        ?.sort((a: any, b: any) => a.createdAt.diff(b.createdAt))
        .slice(-5)
        .map((req: any) => ({
          ...req,
          agencyName: agenciesById[req?.agencyId]?.name,
        })),
    [lastResponseList, myShelter, agenciesById],
  );

  const canAswer = (groupId: string) => {
    return myShelter?.groupInfo?.find(
      it => groupId.includes(it.groupId) && it.canAnswerRequest,
    );
  }

  const createResponse = async (answer: string, requestId: string) => {
    try {
      setIsLoding(true);

      const payload = {
        requestId: requestId,
        response: answer
      };
      const response = await axiosInstance.post(API_ENDPOINTS.BEDFINDER.SENDRESPONSE.replace("{requestId}", requestId), payload);

      if (response.status == 200) {
        updateCachedResponseId(response.data.responseId);
        getLastResponsesList();
        getRequestFromOthers();
        showToast("The request was successfully sent.", "success");
      }
      setIsLoding(false);
    } catch (error) {
      updateCachedResponseId(null);
      setIsLoding(false);
      showToast("An error occurred while submitting the request. Please try again.", "error");
      console.log(error);
    }
  }

  return (
    <div>
      {/* <Loader/> */}
      {/* Render Open Requests */}
      {request.map((req: any, index) => (
        <div key={index} className="card p-0 mt-6 overflow-hidden">
          <div className="card-header flex flex-col md:flex-row md:items-center justify-between p-5 border border-b text-gray-600 text-sm 2xl:text-base font-medium">
            <div className="flex flex-col md:flex-row gap-3 md:items-center md:gap-0 md:space-x-4">
              <div className="flex items-center gap-3">
                <img src={LocateBed} alt="LocateBed" className="w-8" />
                <h3><b className='text-blue-950'>{agenciesById[req.agencyId]?.name}</b> has requested a bed</h3>
              </div>
              <span className="text-blue-100 hidden md:block">|</span>
              <div>
                Request #: <Link to={`/bedfinder/beds-needed/review/${req.requestId}`} className="text-primary underline font-semibold">{req.nr}</Link>
              </div>
            </div>
            <div className="flex items-center mt-3 md:mt-0">
              <FiCalendar className="me-3" size={20} />
              <span>{formatDateTime(req.need.createdAt)}</span>
            </div>
          </div>
          <div className="card-body p-3 lg:p-5">
            <div className="border rounded-xl p-4">
              <div className="fix-block">
                {req.need.assessments[0].age && (
                  <div className="text-xs 2xl:text-sm text-gray-700 font-normal mb-4">
                    Age : <span className="font-semibold to-gray-800 ms-2">{req.need.assessments[0].age}</span>
                  </div>
                )}
                {getOther(req.need.assessments[0], "currentHousingSituation") && (
                  <div className="text-xs 2xl:text-sm text-gray-700 font-normal mb-4">
                    Current housing situation : <span className="font-semibold to-gray-800 ms-2">{getOther(req.need.assessments[0], "currentHousingSituation")}</span>
                  </div>
                )}
                {(req.need.ssessments?.[0]?.accompanyingChildren?.length ?? 0) > 0 && (
                  <div className="text-xs 2xl:text-sm text-gray-700 font-normal mb-4">
                    Children : <span className="font-semibold to-gray-800 ms-2">{getElements(req.need.assessments?.[0]?.accompanyingChildren ?? [])}</span>
                  </div>
                )}
                {req?.need?.assessments?.[0]?.accompanyingChildren && (
                  <div className="text-xs 2xl:text-sm text-gray-700 font-normal mb-4">
                    Children : <span className="font-semibold to-gray-800 ms-2">{getElements(
                      req?.need?.assessments?.[0]?.accompanyingChildren,
                    )}</span>
                  </div>
                )}
                {req.need.assessments?.[0]?.accompanyingChildrenDetails &&
                  req.need.assessments[0].accompanyingChildrenDetails.map((child: any, __id: any) => (
                    <div className="text-xs 2xl:text-sm text-gray-700 font-normal mb-4" key={"details" + __id}>
                      <div>
                        <span>
                          Age  : <span className="font-semibold to-gray-800 ms-2">{child.age}</span>
                        </span>
                      </div>
                    </div>
                  ))}
                <div
                  className={`collapse-content transition-all duration-300 overflow-hidden ${isExpandedArray[index] ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
                    }`}
                >
                  {getElements(req.need.assessments[0].history) && (
                    <div className="text-xs 2xl:text-sm text-gray-700 font-normal mb-4">
                      Violence/aggression/self-harming behaviors :<span className="font-semibold to-gray-800 ms-2">{getElements(req.need.assessments[0].history)}</span>
                    </div>
                  )}
                  {req.need.assessments[0].mentalHealth && (
                    <div className="text-xs 2xl:text-sm text-gray-700 font-normal mb-4">
                      Current mental health :<span className="font-semibold to-gray-800 ms-2">{req.need.assessments[0].mentalHealth}</span>
                    </div>
                  )}
                  {req.need.assessments[0].currentSubstanceUse && (
                    <div className="text-xs 2xl:text-sm text-gray-700 font-normal mb-4">
                      Current substance use :<span className="font-semibold to-gray-800 ms-2">{req.need.assessments[0].currentSubstanceUse}</span>
                    </div>
                  )}
                  {req.need.assessments[0].primaryLanguage && (
                    <div className="text-xs 2xl:text-sm text-gray-700 font-normal mb-4">
                      Primary Language :<span className="font-semibold to-gray-800 ms-2">{req.need.assessments[0].primaryLanguage}</span>
                    </div>
                  )}
                  {req.need.assessments[0].secondaryLanguage && (
                    <div className="text-xs 2xl:text-sm text-gray-700 font-normal mb-4">
                      Secondary Language :<span className="font-semibold to-gray-800 ms-2">{req.need.assessments[0].secondaryLanguage}</span>
                    </div>
                  )}
                  {getOther(req.need.assessments[0], "languageSpoken") && (
                    <div className="text-xs 2xl:text-sm text-gray-700 font-normal mb-4">
                      Language spoken :<span className="font-semibold to-gray-800 ms-2">{getOther(req.need.assessments[0], "languageSpoken")}</span>
                    </div>
                  )}
                  {req.need.assessments[0].englishProficiency && (
                    <div className="text-xs 2xl:text-sm text-gray-700 font-normal mb-4">
                      English Proficiency :<span className="font-semibold to-gray-800 ms-2">{req.need.assessments[0].englishProficiency}</span>
                    </div>
                  )}
                  {getOther(req.need.assessments[0], "traffickingSituation") && (
                    <div className="text-xs 2xl:text-sm text-gray-700 font-normal mb-4">
                      Type of trafficking or violence :<span className="font-semibold to-gray-800 ms-2">{getOther(req.need.assessments[0], "traffickingSituation")}</span>
                    </div>
                  )}
                  {getOther(req.need.assessments[0], "physicalDisability") && (
                    <div className="text-xs 2xl:text-sm text-gray-700 font-normal mb-4">
                      Physical disability or mobility concerns :<span className="font-semibold to-gray-800 ms-2">{getOther(req.need.assessments[0], "physicalDisability")}</span>
                    </div>
                  )}
                  {req.need.assessments[0]?.additionalInformation && (
                    <div className="text-xs 2xl:text-sm text-gray-700 font-normal mb-4">
                      What other information about your client and their situation is helpful in finding appropriate shelter for them? (Optional) :<span className="font-semibold to-gray-800 ms-2">{req.need.assessments[0]?.additionalInformation}</span>
                    </div>
                  )}
                </div>
              </div>
              <button
                className="text-primary font-medium mt-4"
                onClick={() => toggleDetails(index)}
              >
                {isExpandedArray[index] ? 'View Less Detail..' : 'View More Details...'}
              </button>
              {canAswer(req.groupId) && (
                <div className="bottom-block">
                  <p className="text-gray-900 my-4">We may have space:</p>
                  <div className="flex space-x-3">
                    <button className="btn btn-outline-gray px-4 text-xs 2xl:text-sm py-2 md:min-w-24" onClick={() => createResponse("possible", req.requestId)} disabled={isLoding}>Now</button>
                    <button className="btn btn-outline-gray px-4 text-xs 2xl:text-sm py-2 md:min-w-24" onClick={() => createResponse("possible_soon", req.requestId)} disabled={isLoding}>Soon</button>
                    <button className="btn btn-outline-gray px-4 text-xs 2xl:text-sm py-2 md:min-w-24" onClick={() => createResponse("no", req.requestId)} disabled={isLoding}>Not Now</button>
                  </div>
                  <p className="mt-3">
                    Keep in mind: if you click <strong>Now</strong> or <strong>Soon</strong>, the agency or survivor will contact you to make sure your shelter is a good fit.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
      {lastResponseListData?.length > 0 && (

        <div className="card rounded-xl mt-4">
          <p
            className={`text-lg font-semibold pb-3`}
          >
            Recent responses we sent
          </p>
          {lastResponseListData?.map((req, _id) => (
            <div className='flex items-center mb-3 last:mb-0' key={"RespItem" + _id}>
              {req?.responses?.[0]?.response !== "no" && (
                <FiCheck className='text-success text-xl me-2' />
              )}
              {req?.responses?.[0]?.response === "no" && (
                <FiX className='text-error text-xl me-2' />
              )}
              <div className="desc">
                <span>{req.agencyName.trim()}</span>
                <span>
                  &nbsp;has an&nbsp;
                  <Link to={`/bedfinder/beds-needed/review/${req.requestId}`} className='text-success'>{req?.status} request</Link>.
                  You responded:&nbsp;
                </span>
                {req?.responses?.[0]?.response &&
                  req.responses[0].response !== "null" && (
                    <strong>
                      {responseTextByType[req.responses[0].response]}
                    </strong>
                  )}
              </div>
            </div>
          ))}
        </div>

      )}
      {lastResponseListData?.length === 0 && request?.length === 0 && (
        <div className="card mt-3 rounded-lg">There are no new bed request from any other agency. </div>
      )}
    </div>
  );
};

export default BedNeeded;
